import { performOnlineItemDeactivation } from 'src/brm/actions/authorize/authorize-online-items';
import { EmeraldError, EmeraldErrorModal } from 'src/common/api/emerald-error';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { BrmActionType } from 'src/common/enumerations/brm-action-type';
import { EJRecordTypes } from 'src/common/enumerations/ej-record-types';
import { EntryMethod } from 'src/common/enumerations/entry-method';
import {
  BarcodeNotFoundInTransaction,
  ItemNotFoundInTransaction,
  ResourceNotFound,
  TenderAmountExceededEligibilityAmount,
  WicTenderRestriction,
} from 'src/common/helpers/error-code';
import { voidReturn } from 'src/common/helpers/transaction-helper';
import { IMessageModal } from 'src/common/interfaces/dtos/IMessageModal';
import { IElectronicJournalRecord } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { BRMInputActions } from 'src/common/redux/reducers/brm-input-reducer';
import { restartPaymentSession } from 'src/devices/payment/pinpad-operations';
import { CartResponse } from 'src/items-list/business-logic/service/interfaces/cart-response';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { showTenderAmountEligibilityModal } from 'src/price-override/business-logic/handle-price-override';
import { showEwicErrorModal } from 'src/tendering/business-logic/handle-ewic-error-handling';
import { initiateTendering } from 'src/tendering/business-logic/initiate-tendering';
import { EmeraldDialogTypes } from '../../common/enumerations/emerald-dialog-types';
import { OnlineItemVoidAuthorization } from '../../common/helpers/error-code';
import { ICancelLineItem } from '../../common/interfaces/dtos/ICancelLineItem';
import { IItemInputData } from '../../common/interfaces/IItemInputData';
import { store } from '../../common/redux/configure-store';
import { exitTenderingMode } from '../../tendering/business-logic/tendering-actions';
import { applyCartResponse } from './common/apply-cart-response';
import { itemCancel } from './service/item-cancel';
import { lineItemCancel } from './service/line-item-cancel';
import { voidByCode } from './service/void-by-code';
import { validateVoidItemEftTender } from './validate-tender-eft-void-one-item';

export const cancelLine = async (itemToCancel?: IElectronicJournalRecord): Promise<void> => {
  const { transactionIdExists, transactionId } = store.getState().transaction;
  const isTendering = store.getState().tendering.isTendering;
  const selectedItem =
    itemToCancel ?? store.getState().itemList.ejItems.find(item => item.Selected);

  //TODO: We need revert this code when SE fixed the problem check here https://jira.ncr.com/browse/XLR-6744
  if (validateVoidItemEftTender()) {
    popupMessage(
      new EmeraldErrorModal({
        message: 'VoidOneItemOneTender',
        type: EmeraldDialogTypes.Error,
        subMessage: 'VoidOneItemOneTender_SubMessage',
        hideCancelButton: true,
      }),
    );
    return;
  }

  if (transactionIdExists) {
    if (selectedItem && !selectedItem.Voided && selectedItem.Type !== EJRecordTypes.ReturnItem) {
      const inputData: ICancelLineItem = {
        sequenceNumber: selectedItem.SequenceNumber,
        entryMethod: EntryMethod.Tapped,
      };
      if (isTendering) {
        await withSpinner(async () => {
          await exitTenderingMode();
        });
      }
      return lineItemCancel(inputData, transactionId)
        .then(async (cartResponse: CartResponse) => {
          applyCartResponse(cartResponse);
          return cartResponse;
        })
        .then(async (cart: CartResponse) => {
          if (
            isTendering &&
            cart.cartInfo?.totals?.itemsCount !== undefined &&
            cart.cartInfo?.totals?.itemsCount > 0
          ) {
            await initiateTendering();
          }
        })
        .catch(async e => {
          if (e.code === OnlineItemVoidAuthorization) {
            if (inputData.sequenceNumber) {
              const deactivationData = await performOnlineItemDeactivation(selectedItem);
              await restartPaymentSession();
              await lineItemCancel(
                {
                  ...inputData,
                  brmInputs: [
                    {
                      ruleId: e.code,
                      lineId: selectedItem.SequenceNumber.toString(),
                      type: BrmActionType.OnlineItemAuthorization,
                      restrictionType: OnlineItemVoidAuthorization,
                      data: {
                        additionalData: deactivationData.additionalData,
                      },
                    },
                  ],
                },
                transactionId,
              )
                .then(applyCartResponse)
                .catch(popupMessage);
            }
          } else if (e.code === TenderAmountExceededEligibilityAmount) {
            showTenderAmountEligibilityModal(e);
          } else if (e.code === WicTenderRestriction) {
            e.message = 'EwicVoidItem';
            return showEwicErrorModal(e);
          } else {
            popupMessage(e);
          }
        })
        .finally(() => {
          store.dispatch(BRMInputActions.resetState());
        });
    } else if (selectedItem && selectedItem.Type === EJRecordTypes.ReturnItem) {
      return voidReturn(selectedItem.SequenceNumber)
        .then(applyCartResponse)
        .catch(popupMessage);
    }
  }
};

export const cancelItem = async (
  itemToCancel?: IElectronicJournalRecord,
  itemsToRemove?: number,
): Promise<void> => {
  const selectedItem =
    itemToCancel ?? store.getState().itemList.ejItems.find(item => item.Selected);
  const isTendering = store.getState().tendering.isTendering;
  const entryMethod =
    store.getState().itemList.items?.find(item => item.lineId === selectedItem?.SequenceNumber)
      ?.entryMethod ?? undefined;
  if (selectedItem && !selectedItem.Voided) {
    const inputData: ICancelLineItem = {
      sequenceNumber: selectedItem.SequenceNumber,
      itemId: selectedItem.ItemId,
      quantity: selectedItem.Quantity ? selectedItem.Quantity - 1 : undefined,
      entryMethod: entryMethod,
    };
    if (isTendering) {
      await withSpinner(async () => {
        await exitTenderingMode();
      });
    }
    return cancelItemById(inputData, itemsToRemove)
      .then(async () => {
        if (isTendering) {
          await initiateTendering();
        }
      })
      .catch(e => {
        if (e.code === TenderAmountExceededEligibilityAmount) {
          const voucherMessageModal: IMessageModal = {
            message: 'Tender_Restriction',
            subMessage: 'TenderAmountExceededEligibilityAmountVoucher',
            type: EmeraldDialogTypes.Info,
          };
          popupMessage(voucherMessageModal);
        } else if (e.code === WicTenderRestriction) {
          e.message = 'EwicVoidItem';
          return showEwicErrorModal(e);
        } else {
          popupMessage(e);
        }
      })
      .finally(() => {
        store.dispatch(BRMInputActions.resetState());
      });
  }
};

export const cancelItemById = async (
  inputData: ICancelLineItem,
  itemsToRemove?: number,
): Promise<void> => {
  const { transactionIdExists, transactionId } = store.getState().transaction;
  if (transactionIdExists) {
    return itemCancel(inputData, transactionId, itemsToRemove)
      .then(applyCartResponse)
      .catch(err => {
        throw formatError(err, inputData.itemId);
      });
  }
};

export const cancelItemByCode = async (data: IItemInputData): Promise<void> => {
  const { transactionIdExists, transactionId } = store.getState().transaction;
  if (transactionIdExists) {
    const inputData: ICancelLineItem = {
      sequenceNumber: 0,
      itemId: data.itemId,
      quantity: data.quantity,
      entryMethod: data.entryMethod ?? EntryMethod.Keyed,
    };
    const isTendering = store.getState().tendering.isTendering;
    if (isTendering) {
      await withSpinner(async () => {
        await exitTenderingMode();
      });
    }
    await withSpinner(async () =>
      voidByCode(inputData, transactionId)
        .then(async (cartResponse: CartResponse) => {
          applyCartResponse(cartResponse);
          return cartResponse;
        })
        .then(async (cart: CartResponse) => {
          if (
            isTendering &&
            cart.cartInfo?.totals?.itemsCount &&
            cart.cartInfo?.totals?.itemsCount > 0
          ) {
            await initiateTendering();
          }
        })
        .catch(err => {
          throw formatError(err, inputData.itemId);
        })
        .finally(() => {
          store.dispatch(BRMInputActions.resetState());
        }),
    );
  }
};

const formatError = (err: EmeraldError, itemId?: string) => {
  if (err.code === ItemNotFoundInTransaction || err.code === ResourceNotFound) {
    err = new EmeraldErrorModal({
      message: 'ItemNotFoundInTransaction_Message',
      subMessageArgs: { arg: itemId || '' },
      type: EmeraldDialogTypes.Error,
      subMessage: 'ItemNotFoundInTransaction_SubMessage',
    });
  } else if (err.code === BarcodeNotFoundInTransaction) {
    err = new EmeraldErrorModal({
      message: 'BarcodeNotFoundInTransaction',
      messageArgs: { arg: itemId || '' },
      type: EmeraldDialogTypes.Error,
    });
  }
  return err;
};
